import type { Education } from '@seek/ca-myprofile-utils';
import { EducationForm, VerifiedEducationForm } from '@seek/profile-edit-ui';
import React from 'react';

import { onSubmitFail } from 'src/components/Card/onSubmitFail';
import { usePage } from 'src/components/Router/usePage';
import { EDIT } from 'src/consts';
import { useAppConfig } from 'src/modules/appConfig';
import type { ActionName } from 'src/types';

import type { FormValues } from '../types';

interface EducationFormRendererProps {
  activeEducation: Education | undefined;
  isVerifiedEducation: boolean;
  actionName: ActionName;
  onFormDirtyChange: (dirty: boolean) => void;
  submitForm: (params: { dirty: boolean; values: FormValues }) => void;
  submitVerifiedForm: (params: {
    dirty: boolean;
    values: { highlights: string };
  }) => void;
  onCancel: () => void;
  validateOnMount: boolean;
  onManageClick: () => void;
  onCertsyClick: () => void;
  onDelete?: () => void;
}

export const EducationFormRenderer = ({
  activeEducation,
  isVerifiedEducation,
  actionName,
  onFormDirtyChange,
  submitForm,
  submitVerifiedForm,
  onCancel,
  validateOnMount,
  onManageClick,
  onCertsyClick,
  onDelete,
}: EducationFormRendererProps) => {
  const page = usePage();
  const { zone, isUnifiedSite } = useAppConfig();

  const onSubmitFailFn = (errors: string[]) => {
    onSubmitFail(errors, page);
  };

  const educationForm = (
    <EducationForm
      zone={zone}
      initialValues={activeEducation}
      onSubmit={submitForm}
      onCancel={onCancel}
      isEditing={actionName === EDIT}
      onSubmitFail={onSubmitFailFn}
      onFormDirtyChange={onFormDirtyChange}
      validateOnMount={validateOnMount}
      scrollToTopOnMobile={false}
      isUnifiedSite={isUnifiedSite}
      {...(onDelete && { onDelete })}
    />
  );

  const verifiedEducationForm = (
    <VerifiedEducationForm
      onSubmit={submitVerifiedForm}
      onCancel={onCancel}
      initialValues={activeEducation!}
      name={activeEducation?.name.text ?? ''}
      issuingOrganisation={activeEducation?.institute.text ?? ''}
      credentialInfo={activeEducation?.credential?.credentialInfo ?? []}
      status={activeEducation?.status ?? null}
      manageVerificationUrl={activeEducation?.credential?.manageVerificationUrl}
      onManageClick={onManageClick}
      onCertsyClick={onCertsyClick}
      onFormDirtyChange={onFormDirtyChange}
      {...(onDelete && { onDelete })}
    />
  );

  return actionName === EDIT && isVerifiedEducation
    ? verifiedEducationForm
    : educationForm;
};
